/**
 * Country slug mappings for international comparison pages.
 * Maps 2-letter ISO 3166-1 alpha-2 codes ↔ URL-friendly slugs.
 */

export const COUNTRY_SLUGS: Record<string, string> = {
  AT: 'austria',
  AU: 'australia',
  BE: 'belgium',
  BR: 'brazil',
  CA: 'canada',
  CH: 'switzerland',
  CO: 'colombia',
  DE: 'germany',
  DK: 'denmark',
  ES: 'spain',
  FI: 'finland',
  FR: 'france',
  GB: 'united-kingdom',
  IE: 'ireland',
  IL: 'israel',
  IN: 'india',
  IR: 'iran',
  IT: 'italy',
  JP: 'japan',
  KR: 'south-korea',
  MX: 'mexico',
  NL: 'netherlands',
  NO: 'norway',
  NZ: 'new-zealand',
  PL: 'poland',
  PT: 'portugal',
  SA: 'saudi-arabia',
  SE: 'sweden',
  SG: 'singapore',
  TH: 'thailand',
  TR: 'turkey',
  TW: 'taiwan',
};

/** Reverse map: slug → country code */
export const SLUG_TO_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(COUNTRY_SLUGS).map(([code, slug]) => [slug, code])
);

/**
 * ISO 3166-1 numeric → alpha-2 mapping for world GeoJSON matching.
 * world-atlas TopoJSON uses numeric IDs in feature properties.
 */
export const ISO_NUMERIC_TO_ALPHA2: Record<string, string> = {
  '036': 'AU', // Australia
  '040': 'AT', // Austria
  '056': 'BE', // Belgium
  '076': 'BR', // Brazil
  '124': 'CA', // Canada
  '170': 'CO', // Colombia
  '756': 'CH', // Switzerland
  '208': 'DK', // Denmark
  '246': 'FI', // Finland
  '276': 'DE', // Germany
  '724': 'ES', // Spain
  '250': 'FR', // France
  '826': 'GB', // United Kingdom
  '372': 'IE', // Ireland
  '376': 'IL', // Israel
  '356': 'IN', // India
  '364': 'IR', // Iran
  '380': 'IT', // Italy
  '392': 'JP', // Japan
  '410': 'KR', // South Korea
  '484': 'MX', // Mexico
  '528': 'NL', // Netherlands
  '578': 'NO', // Norway
  '554': 'NZ', // New Zealand
  '616': 'PL', // Poland
  '620': 'PT', // Portugal
  '682': 'SA', // Saudi Arabia
  '752': 'SE', // Sweden
  '702': 'SG', // Singapore
  '764': 'TH', // Thailand
  '792': 'TR', // Turkey
  '158': 'TW', // Taiwan
  '840': 'US', // United States (for reference)
};

/** ISO 3166-1 alpha-3 → alpha-2 mapping (some GeoJSON uses alpha-3) */
export const ISO_ALPHA3_TO_ALPHA2: Record<string, string> = {
  AUT: 'AT', AUS: 'AU', BEL: 'BE', BRA: 'BR', CAN: 'CA', CHE: 'CH',
  COL: 'CO', DEU: 'DE', DNK: 'DK', ESP: 'ES', FIN: 'FI', FRA: 'FR',
  GBR: 'GB', IRL: 'IE', ISR: 'IL', IND: 'IN', IRN: 'IR', ITA: 'IT',
  JPN: 'JP', KOR: 'KR', MEX: 'MX', NLD: 'NL', NOR: 'NO', NZL: 'NZ',
  POL: 'PL', PRT: 'PT', SAU: 'SA', SWE: 'SE', SGP: 'SG', THA: 'TH',
  TUR: 'TR', TWN: 'TW', USA: 'US',
};

export function getCountrySlug(countryCode: string): string | undefined {
  return COUNTRY_SLUGS[countryCode];
}

export function getCountryCode(slug: string): string | undefined {
  return SLUG_TO_CODE[slug];
}

/** Get alpha-2 code from a GeoJSON feature's numeric or alpha-3 ID */
export function geoIdToAlpha2(id: string): string | undefined {
  // Try numeric first (world-atlas uses 3-digit numeric strings)
  if (/^\d+$/.test(id)) {
    return ISO_NUMERIC_TO_ALPHA2[id.padStart(3, '0')];
  }
  // Try alpha-3
  if (id.length === 3) {
    return ISO_ALPHA3_TO_ALPHA2[id];
  }
  // Already alpha-2
  if (id.length === 2) {
    return COUNTRY_SLUGS[id] ? id : undefined;
  }
  return undefined;
}
